import { TreeItem, TreeItemProps, TreeViewProps, SimpleTreeView } from '@mui/x-tree-view';
import { Box, Tooltip, Typography } from '@mui/material';
import React, { JSX, useEffect, useMemo, useState } from 'react';

import { findNodeLevels, flatToNested, getNestedPath, moveNode, replaceNodeInTree } from './nodes';
import { HBIcon, HBIconType } from '../HBIcon/HBIcon';

export type RenderTree = {
  id: string;
  pid: string | null;
  name: string;
  icon: HBIconType;
  children?: RenderTree[];
  stateCode?: string | null | undefined | null;
  level?: number | undefined;
  tags?: { id: string; title: string }[];
};

export type ItemComponentProps = {
  count?: number;
  isAnyChildrenSelected?: boolean;
  labelIcon?: HBIconType;
  stateCode?: string | null;
  addNode?: (node: RenderTree, addName?: string, icon?: HBIconType) => void;
  onClickAddChild?: (id: string) => void;
  unVisibleAddButton?: (id: string) => boolean;
};

export type HBTreeViewProps = TreeViewProps<true> & {
  onAddNode?: (node: RenderTree) => void;
  AddComponent?: ({
    addNode,
    moveNode,
    node,
    nodes,
    isRoot,
    selectedNodeId,
  }: {
    addNode: (node: RenderTree, addName?: string, icon?: HBIconType) => void;
    moveNode: (nodes: RenderTree[], selectedNodeId: string, orientation: boolean) => RenderTree[];
    node: RenderTree;
    nodes: RenderTree[];
    isRoot: boolean;
    selectedNodeId: string;
  }) => JSX.Element;
  treeItems: RenderTree[];
  ItemComponent: (props: ItemComponentProps) => JSX.Element;
  selected: string;
  onNodeSelect: (event: React.SyntheticEvent, nodeId: string) => void;
  onClickAddChild?: (id: string) => void;
  unVisibleAddButton?: (id: string) => boolean;
};

const CustomTreeItem = (
  props: TreeItemProps & {
    ContentProps: {
      count: number;
      isAnyChildrenSelected: boolean;
      labelIcon: HBIconType;
      addNode?: (node: RenderTree, addName?: string, icon?: HBIconType) => void;
      onClickAddChild?: (id: string) => void;
      unVisibleAddButton?: (id: string) => boolean;
      stateCode?: string | null;
    };
  },
) => {
  const { ContentProps, ...otherProps } = props;
  return (
    <TreeItem
      {...otherProps}
      slots={{
        collapseIcon: () => <HBIcon type="angleDown" size="small" />,
        expandIcon:
          ContentProps?.count > 0 ? () => <HBIcon type="angleLeft" size="small" /> : undefined,
      }}
    />
  );
};

const renderTree = (
  nodes: RenderTree[],
  selectedNodeId: string,
  ItemComponent: HBTreeViewProps['ItemComponent'],
  addNode?: (node: RenderTree, addName?: string, icon?: HBIconType) => void,
  AddComponent?: HBTreeViewProps['AddComponent'],
  onClickAddChild?: (id: string) => void,
  unVisibleAddButton?: (id: string) => boolean,
) => {
  const checkIfChildrenSelected = (
    nodes: RenderTree[],
    selectedNodeId: string,
    currentNodeId: string,
  ): boolean => {
    const path = getNestedPath(nodes, selectedNodeId);
    const result = path ? path.split('/').includes(currentNodeId) : false;
    return result;
  };

  const setLabel = (
    nodeName: string,
    nodeId: string,
    unVisibleAddButton?: (id: string) => boolean,
  ) => (
    <Tooltip title={nodeName} placement="top">
      <Typography
        sx={{
          pl: (unVisibleAddButton?.(nodeId) ?? false) ? 5 : 0,
        }}
      >
        {nodeName.length > 23 ? nodeName?.substring(0, 22).concat('...') : nodeName}
      </Typography>
    </Tooltip>
  );

  return nodes.map((node: RenderTree) => (
    <Box key={node.id}>
      <CustomTreeItem
        itemId={node.id}
        label={setLabel(node.name, node.id, unVisibleAddButton)}
        ContentProps={{
          count: node.children?.length || 0,
          isAnyChildrenSelected: checkIfChildrenSelected(nodes, selectedNodeId, node.id),
          onClickAddChild,
          unVisibleAddButton,
          stateCode: node?.stateCode,
          labelIcon: node.icon,
          addNode,
        }}
        sx={{ ml: (node?.level ?? 0) > 2 ? 4 : 0 }}
      >
        {AddComponent && addNode && (
          <AddComponent
            addNode={addNode}
            moveNode={moveNode}
            node={node}
            nodes={nodes}
            isRoot={node.pid === null}
            selectedNodeId={selectedNodeId}
          />
        )}
        {Array.isArray(node.children)
          ? renderTree(
              node.children,
              selectedNodeId,
              ItemComponent,
              addNode,
              AddComponent,
              onClickAddChild,
              unVisibleAddButton,
            )
          : null}
      </CustomTreeItem>
    </Box>
  ));
};

export const HBTreeView = (props: HBTreeViewProps): JSX.Element => {
  const {
    treeItems,
    selected,
    AddComponent,
    ItemComponent,
    onAddNode,
    onClickAddChild,
    unVisibleAddButton,
    ...otherProps
  } = props;
  console.log('treeItems', treeItems);
  const [nodes, setNodes] = useState<RenderTree[]>();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([selected]);

  useEffect(() => {
    setNodes(treeItems);
  }, [treeItems]);

  const checkIfNested = (nodes: RenderTree[]) =>
    nodes.some((node) => node.children && node.children.length > 0);

  const nestedTreeItems = useMemo(
    () => (nodes && checkIfNested(nodes) ? nodes : nodes && flatToNested(nodes)),
    [nodes],
  );

  const addNode = (node: RenderTree) => {
    if (nestedTreeItems) {
      const newNode: RenderTree = {
        id: Math.floor(Math.random() / 10000000).toString(),
        pid: node.pid,
        name: node.pid ?? 'کشور',
        icon: node.icon ?? 'camera',
      };
      const nodeLevels = findNodeLevels(nestedTreeItems, node);
      if (!nodeLevels) return;
      const isRoot = node.pid === null;
      nodeLevels.unshift(newNode);
      const test = replaceNodeInTree(nestedTreeItems, nodeLevels);
      setNodes(isRoot ? nodeLevels : test);
      if (onAddNode) {
        onAddNode(node);
      }
    }
  };

  const handleToggle = (event: React.SyntheticEvent, itemIds: string[]) => {
    setExpandedItems(itemIds);
  };

  const handleSelect = (event: React.SyntheticEvent, itemIds: string[]) => {
    setSelectedItems(itemIds);
    if (itemIds.length > 0) {
      otherProps.onNodeSelect(event, itemIds[0]);
    }
  };

  return (
    <SimpleTreeView
      {...otherProps}
      expandedItems={expandedItems}
      onExpandedItemsChange={handleToggle}
      selectedItems={selectedItems}
      onSelectedItemsChange={handleSelect}
    >
      {nestedTreeItems &&
        renderTree(
          nestedTreeItems,
          selected as string,
          ItemComponent,
          addNode,
          AddComponent,
          onClickAddChild,
          unVisibleAddButton,
        )}
      {props.children}
    </SimpleTreeView>
  );
};

HBTreeView.displayName = 'HBTreeView';
